import React, { useMemo } from "react";
import { toFiniteNumber } from "../../services/utilities";
import { buildPeriodAverageSeries } from "./d3ChartShared";
import "./d3-charts.css";

const getDisplayPrice = (mark) => {
  const explicitPrice = toFiniteNumber(mark?.price);
  if (explicitPrice !== null) return explicitPrice;

  const bid = toFiniteNumber(mark?.bid);
  const offer = toFiniteNumber(mark?.offer);
  if (bid === null || offer === null) return null;

  return (bid + offer) / 2;
};

const buildAverageTrendData = (dataArray, { approvedOnly = false } = {}) =>
  buildPeriodAverageSeries({
    rows: dataArray
      .map((mark) => ({
        ...mark,
        chartDate: new Date(mark.date ?? mark.marketDate),
        displayPrice: getDisplayPrice(mark),
      }))
      .filter(
        (mark) =>
          mark.displayPrice != null &&
          (!approvedOnly || mark.markState === "Approved")
      ),
    valueAccessor: (mark) => mark.displayPrice,
  });

const ChartLegend = ({ data = [], chartType = "line" }) => {
  const legendItems = useMemo(() => {
    // Handle case where data might be undefined or null
    const dataArray = Array.isArray(data) ? data : [];
    if (dataArray.length === 0) return [];

    const items = [];
    const approvedPriceClass =
      chartType === "candlestick"
        ? "d3-chart-circle d3-chart-circle-price-approved d3-chart-circle-spot-price"
        : "d3-chart-circle d3-chart-circle-price-approved d3-chart-circle-forward-price";

    // Show moving average for candlestick charts when the candlestick trend line would render.
    if (chartType === "candlestick") {
      const candlestickTrendData = buildAverageTrendData(dataArray, {
        approvedOnly: true,
      });
      const hasCandlestickTrendLine = candlestickTrendData.length > 1;

      if (hasCandlestickTrendLine) {
        items.push({
          type: "line",
          cssClass: "d3-chart-line d3-chart-line-spot-average",
          label: "Moving Average",
        });
      }
    }

    // Check for approved prices
    const hasApprovedPrices = dataArray.some(
      (d) => getDisplayPrice(d) != null && d.markState === "Approved",
    );
    if (hasApprovedPrices) {
      items.push({
        type: "circle",
        cssClass: approvedPriceClass,
        label: "Reported Price",
        radius: 2.5,
      });
    }

    // Check for submitted prices
    const hasSubmittedPrices = dataArray.some(
      (d) => getDisplayPrice(d) != null && d.markState === "Submitted",
    );
    if (hasSubmittedPrices) {
      items.push({
        type: "circle",
        cssClass: "d3-chart-circle d3-chart-circle-price-submitted",
        label: "Submitted Price",
        radius: 2.35,
      });
    }

    // Check for bids
    const hasBids = dataArray.some((d) => d.bid != null);
    if (hasBids) {
      items.push({
        type: "circle",
        cssClass: "d3-chart-circle d3-chart-circle-bid",
        label: "Bid",
        radius: 1.6,
      });
    }

    // Check for offers
    const hasOffers = dataArray.some((d) => d.offer != null);
    if (hasOffers) {
      items.push({
        type: "circle",
        cssClass: "d3-chart-circle d3-chart-circle-offer",
        label: "Offer",
        radius: 1.6,
      });
    }

    if (chartType === "line") {
      // Show moving average for line charts only when the line chart would actually render it.
      const trendData = buildAverageTrendData(dataArray);
      const hasMovingAverageLine = trendData.length > 1;

      if (hasMovingAverageLine) {
        items.push({
          type: "line",
          cssClass: "d3-chart-line d3-chart-line-moving-average",
          label: "Moving Average",
        });
      }
    }

    const movingAverageIndex = items.findIndex(
      (item) => item.label === "Moving Average",
    );
    if (movingAverageIndex > 0) {
      const [movingAverageItem] = items.splice(movingAverageIndex, 1);
      items.unshift(movingAverageItem);
    }

    return items;
  }, [data, chartType]);

  if (legendItems.length === 0) {
    return null;
  }

  return (
    <div className="d3-chart-legend-container">
      <div className="d3-chart-legend">
        {legendItems.map((item, index) => (
          <div
            key={`${item.type}-${item.label}-${index}`}
            className="d3-chart-legend-item"
          >
            <div
              className={`d3-chart-legend-symbol d3-chart-legend-symbol-${item.type} ${item.cssClass}`}
              style={
                item.type === "circle" && item.radius
                  ? {
                      width: `${item.radius * 2}px`,
                      height: `${item.radius * 2}px`,
                    }
                  : {}
              }
            />
            <span className="d3-chart-legend-label">{item.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export default ChartLegend;
